import { useMemo } from 'react';
import { FileSearch } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { PageLayout } from '@/components/shared/PageLayout';
import { PageHeader } from '@/components/shared/PageHeader';
import { EmptyState } from '@/components/shared/EmptyState';
import { ErrorAlert } from '@/components/shared/ErrorAlert';
import { ReferenceHeader } from './ReferenceHeader';
import { AnalyticsKeyIndicator } from './AnalyticsKeyIndicator';
import { GlobalPerformanceCharts } from './GlobalPerformanceCharts';
import { useTranslation } from '@/hooks/useTranslation';
import { useReferenceAlias } from '@/hooks/useReferenceAlias';
import type { AnalyticsErrorDetails } from '../../types';
import type { Tool } from '@/features/traceability/type/Tool';

export type ReferenceAnalyticsLayoutProps = {
  references: string[];
  selectedReference: string;
  onReferenceChange: (value: string | null) => void;
  onRefresh: () => void;
  onCompareClick: () => void;
  isLoading: boolean;
  error?: string | null;
  errorDetails?: AnalyticsErrorDetails | null;
  tools: Tool[];
};

export function ReferenceAnalyticsLayout({
  references,
  selectedReference,
  onReferenceChange,
  onRefresh,
  onCompareClick,
  isLoading,
  error,
  errorDetails,
  tools,
}: ReferenceAnalyticsLayoutProps) {
  const { t } = useTranslation();
  const { resolve } = useReferenceAlias();

  // Filter traces for the selected reference
  const referenceTraces = useMemo(() => {
    if (!selectedReference) return [];
    return tools
      .filter((tool) => tool.reference === selectedReference)
      .flatMap((tool) => tool.traces);
  }, [tools, selectedReference]);

  // Build breadcrumb items with selected reference
  const breadcrumbItems = useMemo(() => {
    const items = [
      { label: t('common.brand') },
      { label: t('analytics.dashboard.breadcrumb.analytics') },
    ];

    if (selectedReference) {
      items.push({ label: resolve(selectedReference).label });
    }

    return items;
  }, [selectedReference, resolve, t]);

  return (
    <PageLayout contentClassName="gap-6 sm:gap-8 max-w-7xl">
      <PageHeader
        breadcrumb={breadcrumbItems}
        title={t('analytics.dashboard.title')}
        description={t('analytics.dashboard.description')}
      />

      <section className="flex flex-col gap-4 sm:gap-6">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          {/* Reference Header - Left side */}
          <div className="flex-1">
            <ReferenceHeader
              references={references}
              selectedReference={selectedReference}
              onReferenceChange={onReferenceChange}
              tools={tools}
              isLoading={isLoading}
            />
          </div>

          {/* Action Buttons - Right side, in column (centered vertically with ReferenceHeader) */}
          <div className="flex flex-col gap-3 lg:w-auto">
            <Button
              className="w-full"
              onClick={onRefresh}
              loading={isLoading}
              disabled={!selectedReference}
              aria-keyshortcuts="r"
              title={t('analytics.dashboard.shortcuts.refresh')}
            >
              {isLoading
                ? t('analytics.dashboard.actions.refreshing')
                : t('analytics.dashboard.actions.refresh')}
            </Button>
            <Button variant="outline" className="w-full" onClick={onCompareClick}>
              {t('analytics.dashboard.actions.compareReferences')}
            </Button>
          </div>
        </div>

        {error && (
          <ErrorAlert
            error={error}
            errorDetails={errorDetails}
            onRetry={onRefresh}
            isRetrying={isLoading}
          />
        )}
      </section>

      {/* Section: Key Indicators */}
      {selectedReference && (
        <AnalyticsKeyIndicator traces={referenceTraces} isLoading={isLoading} />
      )}

      {/* Section: Global Performance Charts */}
      {selectedReference && (
        <GlobalPerformanceCharts
          tools={tools}
          selectedReference={selectedReference}
          isLoading={isLoading}
        />
      )}

      {!isLoading && !selectedReference && (
        <EmptyState
          icon={FileSearch}
          title={t('analytics.dashboard.empty.selectReference')}
          className="min-h-[200px]"
        />
      )}
    </PageLayout>
  );
}
